import  { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

// Website Color Palette
const COLORS = {
  primary: '#2563eb',      // Elegant blue
  secondary: '#7c3aed',    // Soft purple
  accent: '#f59e0b',       // Warm amber
  dark: '#1e293b',         // Deep blue
  light: '#f8fafc',        // Light background
  white: '#ffffff',         // Pure white
  gray: '#64748b',         // Muted gray
  lightGray: '#f1f5f9',    // Very light gray
  gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #2563eb 100%)',
  textGradient: 'linear-gradient(135deg, #1e293b 0%, #2563eb 50%, #7c3aed 100%)'
};

// Styled Components
const ActivitiesContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.light} 50%, ${COLORS.lightGray} 100%);
  overflow-x: hidden;
  position: relative;
`;

const HeroSection = styled.section`
  position: relative;
  height: 60vh;
  min-height: 400px;
  background: ${COLORS.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://images.unsplash.com/photo-1564329494258-3f72215ba175?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(30, 58, 138, 0.7);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
`;



const FilterSection = styled.section`
  padding: 3rem 2rem;
  background: ${COLORS.white};
  position: relative;
  z-index: 10;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid ${COLORS.lightGray};
`;

const FilterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${COLORS.primary};
  background: ${props => props.active ? COLORS.primary : COLORS.white};
  color: ${props => props.active ? COLORS.white : COLORS.primary};
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  
  &:hover {
    background: ${COLORS.primary};
    color: ${COLORS.white};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
  }
`;

const ActivitiesGrid = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
`;

const ActivityCard = styled(motion.div)`
  background: ${COLORS.white};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid ${COLORS.lightGray};
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(37, 99, 235, 0.15);
  }
`;

const ActivityImage = styled.div`
  height: 250px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, transparent 100%);
  }
`;

const ActivityBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.color};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.875rem;
`;

const ActivityContent = styled.div`
  padding: 2rem;
`;

const ActivityTitle = styled.h3`
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const ActivityDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;


const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalImage = styled.div`
  height: 400px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const ModalDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ModalFeatures = styled.ul`
  list-style: none;
  margin-bottom: 2rem;
`;

const ModalFeature = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
  
  &::before {
    content: '✓';
    color: #059669;
    font-weight: bold;
    margin-right: 1rem;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`;



const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #6b7280;
  z-index: 10;
  
  &:hover {
    background: #f3f4f6;
  }
`;

// const AnimatedSection = ({ children }) => {
//   const [ref, inView] = useInView({
//     threshold: 0.1,
//     triggerOnce: true
//   });

//   return (
//     <motion.div
//       ref={ref}
//       initial={{ opacity: 0, y: 50 }}
//       animate={inView ? { opacity: 1, y: 0 } : {}}
//       transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
//     >
//       {children}
//     </motion.div>
//   );
// };

const Activities = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedActivity, setSelectedActivity] = useState(null);

  const categories = [
    { id: 'all', name: 'All Activities' },
    { id: 'water', name: 'Water Sports' },
    { id: 'adventure', name: 'Adventure' },
    { id: 'wellness', name: 'Wellness' },
    { id: 'cultural', name: 'Cultural' }
  ];

  const activities = [
    {
      id: 1,
      title: 'Shikara Ride',
      category: 'water',
      description: 'Experience the traditional Kashmiri boat ride across the pristine waters of Wular Lake.',
      fullDescription: 'Glide peacefully across the crystal-clear waters of Wular Lake in our traditional Shikara boats. This iconic Kashmiri experience offers breathtaking views of the Himalayas and surrounding landscapes. Our experienced boatmen will guide you through hidden coves and share stories about the lake\'s rich history and ecosystem.',
      image: 'https://images.unsplash.com/photo-1564329494258-3f72215ba175?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      duration: '2 hours',
      badge: 'Popular',
      badgeColor: COLORS.primary,
      features: [
        'Traditional wooden Shikara boat',
        'Experienced local boatman',
        'Complimentary Kashmiri Kahwa',
        'Photography opportunities',
        'Safety equipment provided'
      ]
    },
    {
      id: 2,
      title: 'Kayaking Adventure',
      category: 'water',
      description: 'Paddle through the calm waters and explore the lake\'s hidden corners.',
      fullDescription: 'For the more adventurous, our kayaking excursions offer an intimate way to explore Wular Lake. Navigate through narrow channels, discover hidden islands, and get up close with the local wildlife. All equipment and safety briefings are provided.',
      image: 'https://plus.unsplash.com/premium_photo-1661893427047-16f6ddc173f6?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      duration: '3 hours',
      badge: 'Adventure',
      badgeColor: COLORS.secondary,
      features: [
        'Modern kayaking equipment',
        'Professional guide',
        'Safety briefing and equipment',
        'Refreshments included',
        'Photography service available'
      ]
    },
    {
      id: 3,
      title: 'Trekking Expedition',
      category: 'adventure',
      description: 'Hike through the surrounding mountains with experienced guides.',
      fullDescription: 'Explore the breathtaking Himalayan trails surrounding Wular Lake. Our trekking expeditions cater to all fitness levels, from gentle nature walks to challenging mountain hikes. Discover remote villages, ancient temples, and stunning viewpoints.',
      image: 'https://images.unsplash.com/photo-1728052016663-85046766e3a7?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      duration: '6 hours',
      badge: 'Guided',
      badgeColor: COLORS.accent,
      features: [
        'Certified mountain guides',
        'Packed lunch and water',
        'First aid support',
        'Transportation to trailhead',
        'Local cultural insights'
      ]
    },
    {
      id: 4,
      title: 'Yoga & Meditation',
      category: 'wellness',
      description: 'Find your inner peace with lakeside yoga sessions.',
      fullDescription: 'Start your day with rejuvenating yoga and meditation sessions by the lake. Our certified instructors guide you through practices suitable for all levels, helping you connect with nature and find inner peace amidst the stunning Himalayan backdrop.',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
      duration: '1.5 hours',
      badge: 'Wellness',
      badgeColor: COLORS.primary,
      features: [
        'Certified yoga instructor',
        'Yoga mats provided',
        'Meditation cushions',
        'Herbal tea after session',
        'All levels welcome'
      ]
    },
    {
      id: 5,
      title: 'Cultural Village Tour',
      category: 'cultural',
      description: 'Experience authentic Kashmiri village life and traditions.',
      fullDescription: 'Immerse yourself in the rich culture of Kashmir with our guided village tours. Visit local homes, participate in traditional crafts, learn about Kashmiri cuisine, and experience the warm hospitality of the local people.',
      image: 'https://images.unsplash.com/photo-1660600330414-e57837a444a0?q=80&w=736&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      duration: '4 hours',
      badge: 'Cultural',
      badgeColor: COLORS.accent,
      features: [
        'Local guide',
        'Traditional lunch',
        'Craft demonstration',
        'Cultural performance',
        'Shopping opportunities'
      ]
    },
    {
      id: 6,
      title: 'Bird Watching',
      category: 'adventure',
      description: 'Discover the diverse birdlife around Wular Lake.',
      fullDescription: 'Wular Lake is a paradise for bird watchers, home to over 60 species of resident and migratory birds. Join our expert naturalists for guided bird watching tours, with binoculars and field guides provided.',
      image: 'https://images.unsplash.com/photo-1596083332905-666e9acf8807?q=80&w=1229&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      duration: '3 hours',
      badge: 'Nature',
      badgeColor: COLORS.primary,
      features: [
        'Expert naturalist guide',
        'Binoculars provided',
        'Bird identification guide',
        'Photography tips',
        'Best viewing spots'
      ]
    }
  ];

  const filteredActivities = selectedCategory === 'all' 
    ? activities 
    : activities.filter(activity => activity.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <ActivitiesContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroBackground />
        <HeroOverlay />
        <HeroContent>
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Activities & Experiences
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            Discover the magic of Wular Lake through our curated experiences
          </motion.p>
        </HeroContent>
      </HeroSection>

      {/* Filter Section */}
      <FilterSection>
        <FilterContainer>
          {categories.map(category => (
            <FilterButton
              key={category.id}
              active={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.name}
            </FilterButton>
          ))}
        </FilterContainer>
      </FilterSection>

      {/* Activities Grid */}
      <ActivitiesGrid>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <GridContainer>
            <AnimatePresence mode="wait">
              {filteredActivities.map((activity, index) => (
                <ActivityCard
                  key={activity.id}
                  variants={itemVariants}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ y: -10 }}
                  onClick={() => setSelectedActivity(activity)}
                >
                  <ActivityImage src={activity.image}>
                    <ActivityBadge color={activity.badgeColor}>
                      {activity.badge}
                    </ActivityBadge>
                  </ActivityImage>
                  <ActivityContent>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <ActivityDescription>{activity.description}</ActivityDescription>
                  </ActivityContent>
                </ActivityCard>
              ))}
            </AnimatePresence>
          </GridContainer>
        </motion.div>
      </ActivitiesGrid>

      {/* Modal */}
      <AnimatePresence>
        {selectedActivity && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedActivity(null)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={() => setSelectedActivity(null)}>
                ×
              </CloseButton>
              <ModalImage src={selectedActivity.image} />
              <ModalBody>
                <ModalTitle>{selectedActivity.title}</ModalTitle>
                <ModalDescription>{selectedActivity.fullDescription}</ModalDescription>
                <ModalFeatures>
                  {selectedActivity.features.map((feature, index) => (
                    <ModalFeature key={index}>{feature}</ModalFeature>
                  ))}
                </ModalFeatures>
                <ModalFooter>
                </ModalFooter>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </ActivitiesContainer>
  );
};

export default Activities;
